import {Component, Input, OnChanges, OnInit} from '@angular/core';
import {PromoMxObj, ZoneLightPrlObj} from '../../../../../../generated/generated.model';
import {MatTableDataSource} from '@angular/material/table';
import {
    ImageDisplay,
    MxButtonModel,
    MxButtonModule,
    MxCarouselModule,
    MxCreativeTypeService,
    MxGridModule,
    MxThumbnailModule
} from 'mx-core';
import {PageEvent} from '@angular/material/paginator';
import {MatIcon} from '@angular/material/icon';
import {MatTooltipModule} from '@angular/material/tooltip';
import {PrlTranslateService} from '../../../../shared/services/prl-translate.service';
import {MatDialog, MatDialogModule} from '@angular/material/dialog';
import {PrlZonePreviewDialogComponent} from '../prl-zone-preview-dialog/prl-zone-preview-dialog.component';
import {PrlZoneDataService} from '../../../../shared/services/prl-zone-data-service';
import {catchError, EMPTY, finalize} from 'rxjs';
import {PrlGenerateCodeComponent} from '../prl-generate-code/prl-generate-code.component';

@Component({
    selector: 'prl-zones-gallery',
    standalone: true,
    imports: [
        MxGridModule,
        MxButtonModule,
        MatIcon,
        MatTooltipModule,
        MxThumbnailModule,
        MatDialogModule,
        MxCarouselModule,
    ],
    templateUrl: './prl-zones-gallery.component.html',
    styleUrl: './prl-zones-gallery.component.scss',
})
export class PrlZonesGalleryComponent implements OnChanges, OnInit {
    @Input() lightZones: ZoneLightPrlObj[] = [];

    public dataSource = new MatTableDataSource<ZoneLightPrlObj>();

    generateCodeBtn: MxButtonModel;

    creativeDescriptionLabel = '';
    zoneDescriptionLabel = '';

    pageIndex = 0;
    pageSize = 10;

    previewLoadingPromoId?: number;
    selectedPromoIndex = new Map<number, number>();

    constructor(
        private readonly translateService: PrlTranslateService,
        private readonly dialog: MatDialog,
        private readonly zoneDataService: PrlZoneDataService,
    ) {
    }

    ngOnChanges(): void {
        this.dataSource.data = this.lightZones;
    }

    getPagedZones(): ZoneLightPrlObj[] {
        const start = this.pageIndex * this.pageSize;
        const end = start + this.pageSize;

        return this.lightZones ? this.lightZones.slice(start, end) : [];
    }

    getSelectedPromo(zone: ZoneLightPrlObj): PromoMxObj {
        const index = this.selectedPromoIndex.get(zone.id) ?? 0;
        return zone.promos[index];
    }

    hasDescription(zone: ZoneLightPrlObj, promo: PromoMxObj): boolean {
        return !!(promo?.description || zone?.description);
    }

    buildDescriptionTooltip(zone: ZoneLightPrlObj, promo: PromoMxObj): string {
        const parts: string[] = [];

        if (promo?.description) {
            parts.push(`${this.creativeDescriptionLabel}: ${promo.description}`);
        }
        if (zone?.description) {
            parts.push(`${this.zoneDescriptionLabel}: ${zone.description}`);
        }

        return parts.join('\n');
    }

    pageSelected(event: PageEvent): void {
        this.pageIndex = event.pageIndex;
        this.pageSize = event.pageSize;
    }

    getThumbnailClass(promo: PromoMxObj): string {
        return MxCreativeTypeService.getDefaultThumbnailClass(promo.typeName);
    }

    getThumbnailUrl(promo: PromoMxObj): string | undefined {
        return promo?.id ? this.zoneDataService.getThumbnailUrl(promo.id) : undefined;
    }

    getThumbnailsForPromos(promos: PromoMxObj[]): ImageDisplay[] {
        return (promos ?? [])
            .map(p => this.getThumbnailUrl(p))
            .filter((url): url is string => !!url)
            .map(url => new ImageDisplay(url));
    }

    onImageChange(zoneId: number, index: number): void {
        this.selectedPromoIndex.set(zoneId, index);
    }


    ngOnInit(): void {
        this.generateCodeBtn = {
            label: () => this.translateService.getTranslatedLabel('label.marketing.tools.generate.code'),
            tooltip: () => this.translateService.getTranslatedLabel('label.marketing.tools.generate.code'),
            className: 'mxs-button--primary-full-width'
        };

        this.creativeDescriptionLabel =
            this.translateService.getTranslatedLabel('label.marketing.tools.zone.creative')
            + ' '
            + this.translateService.getTranslatedLabel('label.marketing.tools.zone.description');
        this.zoneDescriptionLabel =
            this.translateService.getTranslatedLabel('label.marketing.tools.zone.description');
    }

    openPreview(promo: PromoMxObj): void {
        if (!promo?.id || this.previewLoadingPromoId === promo.id) {
            return;
        }

        this.previewLoadingPromoId = promo.id;

        this.zoneDataService.getPromoPreview(promo.id)
            .pipe(
                catchError(() => {
                    this.openPreviewDialog(promo);
                    return EMPTY;
                }),
                finalize(() => {
                    this.previewLoadingPromoId = undefined;
                }),
            )
            .subscribe(res => {
                if (res.status === 200 && res.body) {
                    const previewUrl = URL.createObjectURL(res.body);

                    const dialogRef = this.openPreviewDialog(promo, previewUrl);

                    dialogRef.afterClosed().subscribe(() => {
                        URL.revokeObjectURL(previewUrl);
                    });

                    return;
                }

                this.openPreviewDialog(promo);
            });
    }

    generateCode(zone: ZoneLightPrlObj): void {
        this.dialog.open(PrlGenerateCodeComponent, {
            width: '80%',
            panelClass: 'mx-dialog-panel-class',
            data: {
                zone
            }
        }).afterClosed();
    }

    private openPreviewDialog(promo: PromoMxObj, previewUrl?: string) {
        return this.dialog.open(PrlZonePreviewDialogComponent, {
            data: {
                title: promo.name,
                previewUrl,
                typeName: promo.typeName,
                defaultThumbnailClass: this.getThumbnailClass(promo),
                loading: false,
            },
            width: '900px',
            maxWidth: '95vw',
            panelClass: 'prl-zone-preview-dialog',
        });
    }
}